/**
 * Company: SPHMedia
 * Description: Lifestyle Section Layout
 */

import { ReactElement } from "react";
import { Helmet } from "react-helmet-async";
import { Await } from "react-router-dom";
import { AdvertisementTypeEnum } from "@app/types/enums";
import { LifestyleContext } from "@app/types/Page";
import BtLifestyleLogo from "@assets/logo-btlifestyle.svg";
import Advertisement from "@components/Advertisement/Advertisement";
import PrestitialAdWrapper from "@components/Advertisement/PrestitialAdWrapper";
import { CardVariations } from "@components/ArticleCard/types";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import CustomError from "@components/Error/CustomError";
import FooterSubscribeRow from "@components/Footer/FooterSubscribeRow/FooterSubscribeRow";
// Components import
import Header from "@components/Header/Header";
import { useCategoriesFromPath } from "@components/Header/helpers";
import BtLuxeCarousel from "@components/Slider/BtLuxeSlider";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import LifestyleHero from "@pages/Section/components/Lifestyle/LifestyleHero";
import LifestyleLuxeMasthead from "@pages/Section/components/Lifestyle/LifestyleLuxeMasthead";
import LifestyleNews from "@pages/Section/components/Lifestyle/LifestyleNews";
import SectionDivider from "@pages/Section/components/Lifestyle/SectionDivider";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { sectionNavigationItems } from "@util/constant";
import { useContext } from "@util/getContext";
import { GoogleAdsSlotFactory } from "@util/helpers";

/**
 * Lifestyle
 * Description: Lifestyle Section Layout
 * @param {SectionProps} props
 * @returns {SectionContext}
 */
export type SectionProps = {
  context: LifestyleContext;
};

export default function LifestylePage({
  testId,
}: {
  testId?: string;
}): ReactElement {
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);
  const context: LifestyleContext = useContext();
  const { isPrestitialEnabled, topOverlayImpressions, topOverlayValidity } =
    useGlobalAdSetting({
      targeting: { key: "page", value: "listing" },
    });
  const { parentCategory, childCategory } = useCategoriesFromPath();

  const variant: CardVariations = {
    image: {
      position: "left",
      width: "w-full lg:w-1/2",
      extraClass: "object-cover",
      filters: [
        { breakpoint: "(min-width: 992px)", w: 588, h: 392, dpr: 1, f: "webp" },
        { breakpoint: "(min-width: 768px)", w: 696, h: 464, dpr: 1, f: "webp" },
        { w: 401, h: 267, dpr: 1, f: "webp" },
      ],
    },
    content: {
      width: "w-full lg:w-1/2",
      extraClass: "p-4 bg-black text-white",
    },
    title: {
      size: "text-xl lg:text-6xl",
      color: "text-white",
      extraClass: "text-left font-pt-serif",
    },
    kicker: {
      color: "text-gray-515",
      extraClass: "uppercase",
    },
  };

  return (
    <Await resolve={context} errorElement={<CustomError />}>
      {({ kind, error, data: { lifestyle, lifestyleCarousel } }) => {
        return kind !== "section" || error ? (
          <h1>Error</h1>
        ) : (
          <div className="w-full" data-testid={testId}>
            <Helmet>
              <title>{context.data.title}</title>
            </Helmet>

            <Advertisement
              rootClassName="py-3"
              adUnitProps={{
                type: AdvertisementTypeEnum.LB1,
                slot: GoogleAdsSlotFactory.lb1(parentCategory),
              }}
            />

            <PrestitialAdWrapper
              slot={GoogleAdsSlotFactory.prestitial(parentCategory)}
              {...{
                isPrestitialEnabled,
                topOverlayImpressions,
                topOverlayValidity,
              }}
            />

            <Header
              mastheadTitle={parentCategory}
              mastheadCustomElement={
                <LifestyleLuxeMasthead
                  mastheadPath={parentCategory}
                  logoSrc={BtLifestyleLogo}
                  logoWidth="247"
                  logoHeight="67"
                />
              }
            />

            {parentCategory && sectionNavigationItems?.[parentCategory] ? (
              <div className="flex-none bg-gray-250">
                <Breadcrumb
                  rootClassName="m-auto container"
                  parentCategory={parentCategory}
                  childCategory={childCategory}
                  categoryDataMapping={sectionNavigationItems}
                />
              </div>
            ) : null}

            {lifestyle[0] ? (
              <LifestyleHero
                article={lifestyle[0]}
                defaultImage={{
                  directoryName: lifestyle[0].sections?.at(0)?.uniqueName,
                }}
              />
            ) : null}

            {lifestyle.length ? <SectionDivider /> : null}

            <div className="container px-4">
              <LifestyleNews
                data={lifestyle.slice(1)}
                parentCategory={parentCategory}
                childCategory={childCategory}
              />

              <div className="flex-1 overflow-hidden">
                <BtLuxeCarousel
                  data={lifestyleCarousel}
                  testid="lifestyle-carousel-list"
                  variations={variant}
                  dotsCustomClass="lifestyle-carousel-dots"
                  btLuxeClass="self-center lg:self-start"
                  disablePaywall={
                    OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER
                  }
                />
              </div>
            </div>
            <FooterSubscribeRow />
          </div>
        );
      }}
    </Await>
  );
}
